import { queryKeys } from './index.js';
import { tsr } from '../lib/api.js';

/**
 * Typed subscription + invoice hooks over the ts-rest react-query client
 * (clarity-subscriptions Req 15.1, 15.3, 15.6).
 *
 * Same discipline as the hooks in `./index.ts`: every hook delegates to
 * `tsr.subscriptions.<route>.useQuery/useMutation`, so loading / error / caching
 * stay in the query layer and the admin feature never touches `fetch`. Reads are
 * keyed off the shared `queryKeys` registry (`subscription(clientId)` /
 * `invoices(clientId)`) so each mutation invalidates exactly the lists it affects
 * and the displayed total / invoice rows refresh without a manual refetch.
 *
 * `data` is the ts-rest discriminated `{ status, body }` union; callers narrow on
 * `status === 200/201` to reach the typed `SubscriptionRead` / `Invoice` body.
 */

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * Read the client subscription + computed pricing (enabled-store count,
 * base / add-on / `totalMonthly`; Req 15.1). `enabled` gates the fetch while the
 * client is unresolved, mirroring {@link useBrandsByClient} / {@link useStore}.
 */
export function useSubscription(clientId: string, enabled = true) {
  return tsr.subscriptions.read.useQuery({
    queryKey: queryKeys.subscription(clientId),
    queryData: { params: { clientId } },
    enabled,
  });
}

/** List the client's invoices (period / total / status / due date; Req 15.5). */
export function useSubscriptionInvoices(clientId: string, enabled = true) {
  return tsr.subscriptions.listInvoices.useQuery({
    queryKey: queryKeys.invoices(clientId),
    queryData: { params: { clientId } },
    enabled,
  });
}

// ── Mutations (invalidate on success) ────────────────────────────────────────

/**
 * Create the client subscription (admin only; a second create → 409, Req 1.4).
 * Refreshes the subscription read so the view reflects the new relationship.
 */
export function useCreateSubscription(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.subscriptions.create.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscription(clientId) });
    },
  });
}

/**
 * Set the flat price override (Req 3.2, 15.3); on success the displayed total
 * refreshes to the route's returned value via the subscription-read invalidation.
 */
export function useSetOverride(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.subscriptions.setOverride.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscription(clientId) });
    },
  });
}

/** Clear the override, reverting to the ladder (Req 3.2, 15.3); refreshes the read. */
export function useClearOverride(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.subscriptions.clearOverride.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscription(clientId) });
    },
  });
}

/** Toggle the account-manager add-on (Req 3.5, 15.3); refreshes the read. */
export function useToggleAddon(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.subscriptions.toggleAddon.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscription(clientId) });
    },
  });
}

/** Set the add-on discount, 0–100 (Req 3.6, 15.3); refreshes the read. */
export function useSetAddonDiscount(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.subscriptions.setAddonDiscount.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscription(clientId) });
    },
  });
}

/** Set the billing contact (email / WhatsApp) (Req 13.1); refreshes the read. */
export function useSetBillingContact(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.subscriptions.setBillingContact.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscription(clientId) });
    },
  });
}

/**
 * Record a manual payment and mark the invoice paid (admin only; non-payable →
 * 409, Req 9.4/9.6, 15.6). On success the invoice row flips to `paid`; a `409`
 * / `400` rejects onto `error` and leaves the status unchanged (read it with
 * `errorStatus`). Invalidates BOTH the invoice list (status flip) AND the
 * subscription read, since paying the causing invoice can clear a suspension
 * (Req 9.8), changing the subscription's `status`.
 */
export function useMarkInvoicePaid(clientId: string) {
  const queryClient = tsr.useQueryClient();
  return tsr.subscriptions.markPaid.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscription(clientId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices(clientId) });
    },
  });
}

/**
 * Download the scoped invoice PDF bytes (admin only; Req 15.6). `downloadPdf` is
 * a GET `otherResponse` (`application/pdf`), so there is nothing to cache or
 * invalidate — it is a one-shot imperative fetch the control triggers, not a
 * rendered-on-mount read. We therefore expose the ts-rest route's raw `query`
 * fn directly (not a `useQuery`/`useMutation`): the component awaits it on click
 * and gets back the discriminated `{ status, body }` union — `200` carries the
 * raw `Uint8Array` to wrap in a `Blob` object URL for a browser download, and a
 * `409` (`not_stored`) comes back as an error status to surface (Req 14.6).
 */
export function useDownloadInvoicePdf() {
  return tsr.subscriptions.downloadPdf.query;
}
